import type { Lang } from "../store/authStore";

export interface PackageFeature {
  describe: string;
  active: boolean;
  // "approved" renders a check icon; anything else is shown as text
  value: string;
}

export interface ServicePackage {
  id: number;
  label: string;
  currency: string;
  price: string;
  // Full price used in the WhatsApp order message
  priceFull: string;
  duration: string;
  features: PackageFeature[];
}

// Indonesian visitors see Rupiah (local market); English visitors see USD (overseas clients).
const pricing: Record<Lang, { currency: string; prices: [string, string][] }> = {
  id: {
    currency: "Rp",
    prices: [
      ["3,5 jt", "Rp 3.500.000"],
      ["8,5 jt", "Rp 8.500.000"],
      ["18 jt", "Rp 18.000.000"],
    ],
  },
  en: {
    currency: "USD",
    prices: [
      ["$1,200", "USD 1,200"],
      ["$2,800", "USD 2,800"],
      ["$5,500", "USD 5,500"],
    ],
  },
};

const copy = {
  en: {
    oneTime: "One-time",
    pages: "Number of Pages",
    delivery: "Delivery Time",
    landing: "Landing Page / Portfolio",
    responsive: "Mobile Responsive",
    seo: "SEO Optimized",
    ssl: "SSL Certificate",
    design: "Modern Design",
    contactForm: "Contact Form",
    cms: "CMS Integration",
    fullCms: "Full CMS & Admin Panel",
    consultation: "Free Consultation",
    revisions: "Revisions",
    tiers: [
      { pages: "5 Pages", delivery: "10 Days", revisions: "2 Rounds" },
      { pages: "10 Pages", delivery: "3 Weeks", revisions: "4 Rounds" },
      { pages: "Up to 20 Pages", delivery: "5 Weeks", revisions: "5 Rounds" },
    ],
  },
  id: {
    oneTime: "Sekali bayar",
    pages: "Jumlah Halaman",
    delivery: "Waktu Pengerjaan",
    landing: "Landing Page / Portofolio",
    responsive: "Responsif di Mobile",
    seo: "Optimasi SEO",
    ssl: "Sertifikat SSL",
    design: "Desain Modern",
    contactForm: "Form Kontak",
    cms: "Integrasi CMS",
    fullCms: "CMS Lengkap & Panel Admin",
    consultation: "Konsultasi Gratis",
    revisions: "Revisi",
    tiers: [
      { pages: "5 Halaman", delivery: "10 Hari", revisions: "2 Kali" },
      { pages: "10 Halaman", delivery: "3 Minggu", revisions: "4 Kali" },
      { pages: "Hingga 20 Halaman", delivery: "5 Minggu", revisions: "5 Kali" },
    ],
  },
};

const labels = ["Basic", "Professional", "Premium"];

export const getPackages = (lang: Lang): ServicePackage[] => {
  const c = copy[lang];
  const p = pricing[lang];
  const check = (describe: string): PackageFeature => ({
    describe,
    active: true,
    value: "approved",
  });
  // Third feature row differs per tier
  const tierExtra = [c.contactForm, c.cms, c.fullCms];

  return labels.map((label, i) => ({
    id: i + 1,
    label,
    currency: p.currency,
    price: p.prices[i][0],
    priceFull: p.prices[i][1],
    duration: c.oneTime,
    features: [
      { describe: c.pages, active: true, value: c.tiers[i].pages },
      { describe: c.delivery, active: true, value: c.tiers[i].delivery },
      check(c.landing),
      check(c.responsive),
      check(c.seo),
      check(c.ssl),
      check(c.design),
      check(tierExtra[i]),
      check(c.consultation),
      { describe: c.revisions, active: true, value: c.tiers[i].revisions },
    ],
  }));
};
